import React, { createContext, useContext, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { StoreProptypes } from "../PropTypes/StoreProptypes";

type Theme = "light" | "dark";

interface ThemeContextProps {
  theme: Theme;
  toggleTheme: () => void;
  enableTheme: () => void;
  disableTheme: () => void;
}

const ThemeContext = createContext<ThemeContextProps | undefined>(undefined);

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [theme, setTheme] = useState<Theme>(
    (localStorage.getItem("theme") as Theme) || "light"
  );
  const [enabled, setEnabled] = useState(false);
  const user = useSelector((store: StoreProptypes) => store.user);

  // Reset theme on logout
  useEffect(() => {
    if (!user) {
      setTheme("light");
      setEnabled(false);
      localStorage.removeItem("theme");
      document.body.removeAttribute("data-bs-theme");
    }
  }, [user]);

  useEffect(() => {
    if (!enabled) {
      document.body.removeAttribute("data-bs-theme");
      return;
    }

    document.body.setAttribute("data-bs-theme", theme);
    localStorage.setItem("theme", theme);
  }, [theme, enabled]);

  const toggleTheme = () => {
    setTheme(prev => (prev === "light" ? "dark" : "light"));
  };

  return (
    <ThemeContext.Provider
      value={{
        theme,
        toggleTheme,
        enableTheme: () => setEnabled(true),
        disableTheme: () => setEnabled(false),
      }}
    >
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used within ThemeProvider");
  return ctx;
};
